import { CompanyInfoBanner } from "@/components/CompanyInfoBanner";
import { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Printer, WhatsappLogo, Envelope, FilePdf } from "@phosphor-icons/react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { fmtDate, fmtN } from "@/lib/utils";
import { drawPdfHeader } from "@/lib/pdfHeader";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Reports() {
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [viagens, setViagens] = useState([]);
  const [tripId, setTripId] = useState("");
  const [viagemId, setViagemId] = useState("");
  const [report, setReport] = useState(null);
  const [viagemReport, setViagemReport] = useState(null);
  const [summary, setSummary] = useState(null);
  const [periodReport, setPeriodReport] = useState(null);
  const [periodType, setPeriodType] = useState("month");
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [viagemPdfBusy, setViagemPdfBusy] = useState(false);
  const cardRef = useRef(null);
  const viagemCardRef = useRef(null);

  const downloadPDF = async () => {
    if (!report || !cardRef.current) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      // Header
      await drawPdfHeader(pdf, {
        title: "ACERTO RÁPIDO JVO",
        subtitle: `Relatório de Viagem — ${report.trip.origin || "?"} → ${report.trip.destination || "?"}`,
        photoUrl: user?.photo_url,
        pageW,
      });
      // Body image
      let heightLeft = imgH;
      let position = 26;
      pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
      heightLeft -= (pageH - position);
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - heightLeft;
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= (pageH - margin);
      }
      // Rota map link (external)
      pdf.setPage(pdf.internal.getNumberOfPages());
      pdf.setFontSize(9);
      pdf.setTextColor(37, 99, 235);
      const routeUrl = `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(report.trip.origin || "")}&destination=${encodeURIComponent(report.trip.destination || "")}`;
      pdf.textWithLink("Ver rota no Google Maps ↗", margin, pageH - 8, { url: routeUrl });
      pdf.setTextColor(120, 120, 120);
      pdf.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, pageW - margin, pageH - 8, { align: "right" });

      const filename = `relatorio-${(report.trip.origin || "viagem").replace(/[^\w]/g, "_")}-${(report.trip.destination || "").replace(/[^\w]/g, "_")}-${(report.trip.date_departure || "").replace(/[^\w]/g, "_")}.pdf`;
      pdf.save(filename);
      toast.success("PDF gerado!");
    } catch (e) {
      toast.error("Erro ao gerar PDF: " + (e.message || "desconhecido"));
    } finally { setPdfBusy(false); }
  };

  const downloadViagemPDF = async () => {
    if (!viagemReport || !viagemCardRef.current) return;
    setViagemPdfBusy(true);
    try {
      const canvas = await html2canvas(viagemCardRef.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      await drawPdfHeader(pdf, {
        title: "ACERTO RÁPIDO JVO",
        subtitle: `Relatório da Viagem ${viagemReport.viagem?.viagem_number || ""} — ${viagemReport.fretes?.length || 0} frete(s)`,
        photoUrl: user?.photo_url,
        pageW,
      });
      let heightLeft = imgH;
      let position = 26;
      pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
      heightLeft -= (pageH - position);
      while (heightLeft > 0) {
        pdf.addPage();
        position = margin - heightLeft;
        pdf.addImage(imgData, "PNG", margin, position, imgW, imgH);
        heightLeft -= (pageH - margin);
      }
      pdf.setPage(pdf.internal.getNumberOfPages());
      pdf.setFontSize(9); pdf.setTextColor(120, 120, 120);
      pdf.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, pageW - margin, pageH - 8, { align: "right" });
      const filename = `relatorio-viagem-${viagemReport.viagem?.viagem_number || "x"}-${(viagemReport.viagem?.date_start || "").slice(0, 10)}.pdf`;
      pdf.save(filename);
      toast.success("PDF da viagem gerado!");
    } catch (e) {
      toast.error("Erro ao gerar PDF: " + (e.message || "desconhecido"));
    } finally { setViagemPdfBusy(false); }
  };

  const buildViagemText = () => {
    if (!viagemReport) return "";
    const v = viagemReport;
    const fretesTxt = (v.fretes || []).map((f) =>
      `  • F${f.frete_number} ${f.origin || "?"} → ${f.destination || "?"} · ${f.tons || 0}t · ${f.client || "-"} · ${fmt(f.total_freight)}`
    ).join("\n");
    return `ACERTO RÁPIDO JVO — Relatório da Viagem ${v.viagem?.viagem_number || ""}
Data início: ${fmtDate(v.viagem?.date_start)}   Status: ${v.viagem?.status || "-"}
KM: ${v.viagem?.km_start || 0} → ${v.viagem?.km_end || "?"} (rodados: ${v.km})
Média (km/L): ${v.avg_km_per_liter}
Toneladas totais: ${v.tons}
Diesel: ${v.diesel_liters}L (${fmt(v.diesel_value)})
ARLA: ${v.arla_liters}L (${fmt(v.arla_value)})
Despesas: ${fmt(v.expenses_total)}
Frete bruto (soma): ${fmt(v.gross_freight)}
Frete líquido (soma): ${fmt(v.net_freight)}
Comissão (soma): ${fmt(v.commission)}
🎁 Bonificações: ${fmt(v.bonuses_total || 0)}
Saldo motorista: ${fmt(v.driver_balance)}
Lucro empresa: ${fmt(v.company_profit)}

Fretes realizados nesta viagem (${(v.fretes || []).length}):
${fretesTxt}`;
  };

  const shareViagem = (channel) => {
    const text = encodeURIComponent(buildViagemText());
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${text}`, "_blank");
    if (channel === "email") window.open(`mailto:?subject=Relatório Viagem&body=${text}`, "_blank");
    if (channel === "print") window.print();
  };

  useEffect(() => { api.get("/trips").then((r) => setTrips(r.data)); api.get("/viagens").then((r) => setViagens(r.data)); api.get("/analysis/summary").then((r) => setSummary(r.data)); }, []);
  useEffect(() => { if (tripId) api.get(`/reports/trip/${tripId}`).then((r) => setReport(r.data)); }, [tripId]);
  useEffect(() => { if (viagemId) api.get(`/reports/viagem/${viagemId}`).then((r) => setViagemReport(r.data)); }, [viagemId]);
  useEffect(() => {
    const params = periodType === "month" ? { period: "month", year: selYear, month: selMonth } : { period: "year", year: selYear };
    api.get("/reports/period", { params }).then((r) => setPeriodReport(r.data)).catch(() => {});
  }, [periodType, selYear, selMonth]);

  const buildText = () => {
    if (!report) return "";
    const t = report.trip;
    return `ACERTO RÁPIDO JVO — Relatório de Viagem
Rota: ${t.origin} → ${t.destination}
Data: ${fmtDate(t.date_departure)}
Cliente: ${t.client}
KM rodados: ${report.km}
Média (km/L): ${report.avg_km_per_liter}
Toneladas: ${report.tons}
Preço/ton: ${fmt(report.price_per_ton)}
Frete bruto: ${fmt(report.gross_freight)}
Frete líquido: ${fmt(report.net_freight)}
Diesel: ${report.diesel_liters}L (${fmt(report.diesel_value)})
ARLA: ${report.arla_liters}L (${fmt(report.arla_value)})
Despesas: ${fmt(report.expenses_total)}
Comissão motorista: ${fmt(report.commission)}
Adiantamento: ${fmt(report.advance_driver)}
Despesas caminhão (motorista): ${fmt(report.truck_expenses)}
Saldo motorista: ${fmt(report.driver_balance)}
Lucro empresa: ${fmt(report.company_profit)}`;
  };

  const share = (channel) => {
    const text = encodeURIComponent(buildText());
    if (channel === "whatsapp") window.open(`https://wa.me/?text=${text}`, "_blank");
    if (channel === "email") window.open(`mailto:?subject=Relatório Viagem&body=${text}`, "_blank");
    if (channel === "print") window.print();
  };

  return (
    <div className="space-y-6 print:bg-white">
      <CompanyInfoBanner />
      <div className="print:hidden">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <FileText size={32} weight="duotone" className="text-blue-600" /> Relatórios
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Por viagem, por mês e por ano</p>
      </div>

      <Tabs defaultValue="viagem">
        <TabsList className="h-auto p-1 print:hidden flex-wrap">
          <TabsTrigger value="viagem" data-testid="rep-tab-viagem" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Por Viagem</TabsTrigger>
          <TabsTrigger value="trip" data-testid="rep-tab-trip" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Por Frete</TabsTrigger>
          <TabsTrigger value="month" data-testid="rep-tab-month" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Por Mês</TabsTrigger>
          <TabsTrigger value="year" data-testid="rep-tab-year" className="py-2 px-4 data-[state=active]:bg-blue-600 data-[state=active]:text-white">Por Ano</TabsTrigger>
        </TabsList>

        <TabsContent value="viagem">
          <div className="mb-4 max-w-md print:hidden">
            <Select value={viagemId} onValueChange={setViagemId}>
              <SelectTrigger data-testid="rep-viagem-select"><SelectValue placeholder="Escolha uma viagem" /></SelectTrigger>
              <SelectContent>{viagens.map(v => <SelectItem key={v.id} value={v.id}>Viagem {v.viagem_number} — {fmtDate(v.date_start)} ({v.status})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {viagemReport && (
            <Card ref={viagemCardRef}>
              <CardHeader className="flex-row items-center justify-between">
                <CardTitle>Viagem {viagemReport.viagem?.viagem_number} — {viagemReport.fretes.length} frete(s)</CardTitle>
                <div className="flex items-center gap-3">
                  <div className="flex gap-2 print:hidden flex-wrap">
                    <Button size="sm" variant="outline" onClick={downloadViagemPDF} disabled={viagemPdfBusy} data-testid="btn-viagem-pdf" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1"><FilePdf size={16} weight="duotone" /> {viagemPdfBusy ? "Gerando..." : "PDF"}</Button>
                    <Button size="sm" variant="outline" onClick={() => shareViagem("print")} data-testid="btn-viagem-print"><Printer size={16} className="mr-1" /> Imprimir</Button>
                    <Button size="sm" variant="outline" onClick={() => shareViagem("whatsapp")} data-testid="btn-viagem-whatsapp" className="text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"><WhatsappLogo size={16} className="mr-1" /> WhatsApp</Button>
                    <Button size="sm" variant="outline" onClick={() => shareViagem("email")} data-testid="btn-viagem-email"><Envelope size={16} className="mr-1" /> E-mail</Button>
                  </div>
                  {user?.photo_url && (
                    <img
                      src={user.photo_url}
                      alt={user.role === "company" ? "Logo da empresa" : "Foto do motorista"}
                      width={48}
                      height={48}
                      crossOrigin="anonymous"
                      className="w-12 h-12 rounded-full border-2 border-blue-300 object-cover shadow-sm"
                      data-testid="report-user-logo"
                    />
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  {[
                    ["KM rodados (total)", fmtN(viagemReport.km, 0)],
                    ["Média km/L", fmtN(viagemReport.avg_km_per_liter, 2)],
                    ["Toneladas (soma)", fmtN(viagemReport.tons, 3)],
                    ["Frete bruto (soma)", fmt(viagemReport.gross_freight)],
                    ["Frete líquido (soma)", fmt(viagemReport.net_freight)],
                    ["Comissão (soma)", fmt(viagemReport.commission)],
                    ["🎁 Bonificações", fmt(viagemReport.bonuses_total || 0)],
                    ["Diesel", `${fmtN(viagemReport.diesel_liters, 2)}L — ${fmt(viagemReport.diesel_value)}`],
                    ["ARLA", `${fmtN(viagemReport.arla_liters, 2)}L — ${fmt(viagemReport.arla_value)}`],
                    ["Despesas", fmt(viagemReport.expenses_total)],
                  ].map(([l, v]) => (
                    <div key={l} className="border-b pb-2">
                      <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
                      <div className="font-semibold text-base">{v}</div>
                    </div>
                  ))}
                  <div className="border-2 border-emerald-600 rounded-md p-3 bg-emerald-50 md:col-span-3 flex justify-between items-center">
                    <div><div className="text-xs uppercase tracking-wider text-emerald-800">Saldo Motorista</div><div className="text-2xl font-extrabold text-emerald-900">{fmt(viagemReport.driver_balance)}</div></div>
                    <div className="text-right"><div className="text-xs uppercase tracking-wider text-emerald-800">Lucro Empresa</div><div className="text-2xl font-extrabold text-emerald-900">{fmt(viagemReport.company_profit)}</div></div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">Fretes desta viagem ({viagemReport.fretes.length})</div>
                  <div className="border rounded-md divide-y" data-testid="viagem-fretes-list">
                    {viagemReport.fretes.map((f) => (
                      <div key={f.id} className="p-3 text-sm space-y-1.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-mono font-bold text-blue-700">F{f.frete_number}</span>
                          <span className="flex-1 min-w-[120px]">{f.origin || "?"} → {f.destination || "?"}</span>
                          <span className="text-slate-600">{fmtN(f.tons, 3)}t · {f.client || "-"}</span>
                          <span className="font-semibold text-slate-900">{fmt(f.total_freight)}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-xs text-slate-600 pl-3 border-l-2 border-blue-100">
                          <div><span className="text-slate-500">R$/Ton:</span> <b>{fmt(f.price_per_ton)}</b></div>
                          <div><span className="text-slate-500">Líquido:</span> <b>{fmt(f.net_freight)}</b></div>
                          <div><span className="text-slate-500">Comissão:</span> <b>{fmt(f.commission_value)}</b></div>
                          <div><span className="text-slate-500">Adiantamento:</span> <b>{fmt(f.advance_driver)}</b></div>
                        </div>
                      </div>
                    ))}
                    {viagemReport.fretes.length === 0 && (
                      <div className="p-3 text-sm text-slate-500 italic">Nenhum frete lançado nesta viagem ainda.</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="trip">
          <div className="mb-4 max-w-md print:hidden">
            <Select value={tripId} onValueChange={setTripId}>
              <SelectTrigger data-testid="rep-trip-select"><SelectValue placeholder="Escolha uma viagem" /></SelectTrigger>
              <SelectContent>{trips.map(t => <SelectItem key={t.id} value={t.id}>{t.origin} → {t.destination} ({fmtDate(t.date_departure)})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {report && (
            <Card ref={cardRef}><CardHeader className="flex-row items-center justify-between">
              <CardTitle>Viagem {report.trip.origin} → {report.trip.destination}</CardTitle>
              <div className="flex items-center gap-3">
                <div className="flex gap-2 print:hidden flex-wrap">
                  <Button size="sm" variant="outline" onClick={downloadPDF} disabled={pdfBusy} data-testid="btn-pdf" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1"><FilePdf size={16} weight="duotone" /> {pdfBusy ? "Gerando..." : "PDF"}</Button>
                  <Button size="sm" variant="outline" onClick={() => share("print")} data-testid="btn-print"><Printer size={16} className="mr-1" /> Imprimir</Button>
                  <Button size="sm" variant="outline" onClick={() => share("whatsapp")} data-testid="btn-whatsapp" className="text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"><WhatsappLogo size={16} className="mr-1" /> WhatsApp</Button>
                  <Button size="sm" variant="outline" onClick={() => share("email")} data-testid="btn-email"><Envelope size={16} className="mr-1" /> E-mail</Button>
                </div>
                {user?.photo_url && (
                  <img
                    src={user.photo_url}
                    alt={user.role === "company" ? "Logo da empresa" : "Foto do motorista"}
                    width={48}
                    height={48}
                    crossOrigin="anonymous"
                    className="w-12 h-12 rounded-full border-2 border-blue-300 object-cover shadow-sm"
                    data-testid="report-user-logo-trip"
                  />
                )}
              </div>
            </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-4 text-sm">
                {[
                  ["KM rodados", fmtN(report.km, 0)], ["Média km/L", fmtN(report.avg_km_per_liter, 2)],
                  ["Toneladas", fmtN(report.tons, 3)], ["R$/ton", fmt(report.price_per_ton)],
                  ["Frete bruto", fmt(report.gross_freight)], ["Frete líquido", fmt(report.net_freight)],
                  ["Diesel (L)", `${fmtN(report.diesel_liters, 2)} — ${fmt(report.diesel_value)}`],
                  ["ARLA (L)", `${fmtN(report.arla_liters, 2)} — ${fmt(report.arla_value)}`],
                  ["Despesas", fmt(report.expenses_total)],
                  ["Comissão", fmt(report.commission)],
                  ["Adiantamento", fmt(report.advance_driver)],
                  ["Desp. caminhão", fmt(report.truck_expenses)],
                ].map(([l, v]) => (
                  <div key={l} className="border-b border-border/60 pb-2">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
                    <div className="font-semibold text-base">{v}</div>
                  </div>
                ))}
                <div className="border-2 border-emerald-600 rounded-md p-3 bg-emerald-50 md:col-span-3 flex justify-between items-center">
                  <div><div className="text-xs uppercase tracking-wider text-emerald-800">Saldo Motorista</div><div className="text-2xl font-extrabold text-emerald-900">{fmt(report.driver_balance)}</div></div>
                  <div className="text-right"><div className="text-xs uppercase tracking-wider text-emerald-800">Lucro Empresa</div><div className="text-2xl font-extrabold text-emerald-900">{fmt(report.company_profit)}</div></div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="month">
          <div className="mb-4 flex gap-2 print:hidden">
            <Select value={String(selYear)} onValueChange={(v) => { setPeriodType("month"); setSelYear(Number(v)); }}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{[2024,2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(selMonth)} onValueChange={(v) => { setPeriodType("month"); setSelMonth(Number(v)); }}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>{["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"].map((m,i)=><SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {periodReport && <PeriodCard title={`Resumo — ${["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][selMonth-1]}/${selYear}`} data={periodReport} />}
        </TabsContent>
        <TabsContent value="year">
          <div className="mb-4 flex gap-2 print:hidden">
            <Select value={String(selYear)} onValueChange={(v) => { setPeriodType("year"); setSelYear(Number(v)); }}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>{[2024,2025,2026,2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {periodType === "year" && periodReport && <PeriodCard title={`Resumo Anual ${selYear}`} data={periodReport} />}
          {periodType !== "year" && <p className="text-sm text-muted-foreground">Selecione o ano para carregar o relatório anual.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SummaryCard({ title, summary }) {
  return (
    <Card><CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="grid md:grid-cols-4 gap-4 text-sm">
        {[
          ["Viagens", summary.trip_count],
          ["Toneladas", summary.total_tons],
          ["KM rodados", summary.total_km],
          ["Bruto", fmt(summary.total_gross_freight)],
          ["Líquido", fmt(summary.total_net_freight)],
          ["Comissões", fmt(summary.total_commission)],
          ["Diesel", fmt(summary.total_diesel)],
          ["ARLA", fmt(summary.total_arla)],
          ["Despesas", fmt(summary.total_expenses)],
          ["Lucro Empresa", fmt(summary.company_profit)],
        ].map(([l, v]) => (
          <div key={l} className="border-b border-border/60 pb-2">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className="font-semibold text-base">{v}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PeriodCard({ title, data }) {
  const BONUS_LABELS = {
    on_time_trip: "⏱️ Cumpriu horário",
    professionalism: "🌟 Profissionalismo",
    best_avg_trip: "⛽ Melhor média viagem",
    best_avg_month: "🏆 Melhor média mês",
    best_avg_year: "🥇 Melhor média ano",
    standby_daily: "🕒 Diária tempo parado",
    other: "➕ Outro",
  };
  const hasBonuses = (data.total_bonuses || 0) > 0;
  return (
    <Card><CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          {[
            ["Viagens", data.viagem_count],
            ["Fretes", data.frete_count],
            ["Toneladas", fmtN(data.total_tons, 3)],
            ["KM rodados", fmtN(data.total_km, 0)],
            ["Bruto", fmt(data.total_gross_freight)],
            ["Líquido", fmt(data.total_net_freight)],
            ["Comissões", fmt(data.total_commission)],
            ["🎁 Bonificações", fmt(data.total_bonuses || 0)],
            ["Diesel", fmt(data.total_diesel)],
            ["ARLA", fmt(data.total_arla)],
            ["Despesas", fmt(data.total_expenses)],
          ].map(([l, v]) => (
            <div key={l} className="border-b border-border/60 pb-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
              <div className="font-semibold text-base">{v}</div>
            </div>
          ))}
          <div className="border-2 border-emerald-600 rounded-md p-3 bg-emerald-50 md:col-span-4 flex justify-between items-center">
            <div><div className="text-xs uppercase tracking-wider text-emerald-800">Lucro Empresa (período)</div></div>
            <div className="text-2xl font-extrabold text-emerald-900">{fmt(data.company_profit)}</div>
          </div>
        </div>
        {hasBonuses && data.bonuses_by_type && Object.keys(data.bonuses_by_type).length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">🎁 Bonificações por tipo</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="period-bonuses-by-type">
              {Object.entries(data.bonuses_by_type).map(([k, v]) => (
                <div key={k} className="bg-emerald-50 border border-emerald-200 rounded p-2 text-sm">
                  <div className="text-xs text-emerald-700">{BONUS_LABELS[k] || k}</div>
                  <div className="font-bold text-emerald-900">{fmt(v)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.viagens?.length > 0 && (
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">Viagens do período</div>
            <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
              {data.viagens.map((v) => (
                <div key={v.id} className="flex items-center justify-between p-2 text-xs gap-2">
                  <span className="font-mono font-bold text-blue-700">V{v.viagem_number}</span>
                  <span>{v.date_start || "?"} → {v.date_end || "em aberto"}</span>
                  <span className="text-slate-600">KM: {v.km_start} → {v.km_end || "?"}</span>
                  <span className={v.status === "finalizada" ? "text-emerald-700 font-semibold" : "text-amber-700"}>{v.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
